import { useFlags } from 'launchdarkly-react-client-sdk';
import { FileUp, Settings } from 'lucide-react';
import { ComponentProps } from 'react';
import { Button } from '../../design-system/button/button';
import { Inline } from '../../design-system/inline/inline';
import { Stack } from '../../design-system/stack/stack';
import { InvoiceEvidenceView } from '../../types/entity-types';
import { SupportedCountryCurrency } from '../../types/supported-country-currency';
import { InvoicesMetricCard, MetricCardVariant } from './components/empty-state/invoices-metric-card';
import { InvoiceDataSection } from './components/invoice-data-section/invoice-data-section';
import { InvoiceDrawerContentBodyVariant } from './components/invoice-drawer/invoice-drawer-content-body';
import { CardVariant, InvoiceScanningProgressCard } from './components/invoice-scanning-progress-card';
import { OAuthConnectionBadge } from './components/oauth-connection-badge';

type InvoicesMetrics = {
  variant: MetricCardVariant;
  metric: number;
  normalizedAmount: number;
  currency: SupportedCountryCurrency;
};

type EmailScanningProgressConfig =
  | { isVisible: false }
  | {
      isVisible: true;
      status: CardVariant;
      invoiceCount: number;
      onHideCard: () => void;
    };

type InvoicePayPageProps = {
  evidenceDataConfig: ComponentProps<typeof InvoiceDataSection>['evidenceDataConfig'];
  emailConnectionConfig: ComponentProps<typeof OAuthConnectionBadge>;
  xeroConnectionConfig: ComponentProps<typeof OAuthConnectionBadge>;
  invoicesMetrics: InvoicesMetrics[];
  emailScanningProgressConfig: EmailScanningProgressConfig;
  isFetchingDocumentUrl: boolean;
  selectedEvidenceId?: string;
  approvingEvidenceId?: string[];
  onClickSettings: () => void;
  onUploadManualInvoice: () => void;
  onReviewInvoice: (invoiceId: string, variant: InvoiceDrawerContentBodyVariant) => void;
  onSelectMenuItem: ComponentProps<typeof InvoiceDataSection>['onSelectMenuItem'];
  onDataSorted: (sortedEvidence: InvoiceEvidenceView[]) => void;
  onApproveInvoice: (invoiceIds: string[]) => Promise<void>;
  onReviewTransaction: (transactionId: string) => void;
};

export const InvoicePayPage = ({
  evidenceDataConfig,
  emailConnectionConfig,
  xeroConnectionConfig,
  invoicesMetrics,
  emailScanningProgressConfig,
  isFetchingDocumentUrl,
  selectedEvidenceId,
  approvingEvidenceId,
  onClickSettings,
  onUploadManualInvoice,
  onReviewInvoice,
  onSelectMenuItem,
  onDataSorted,
  onApproveInvoice,
  onReviewTransaction,
}: InvoicePayPageProps) => {
  const { isLongXeroFlowLive } = useFlags<{ isLongXeroFlowLive: boolean }>();

  return (
    <Stack space={10} align="stretch" className="px-32">
      <Inline justify="between" className="h-[42px]">
        <h1 className="text-2xl font-semibold text-black">Invoice Pay</h1>

        <Inline space={2}>
          <OAuthConnectionBadge {...emailConnectionConfig} />

          {!isLongXeroFlowLive && <OAuthConnectionBadge {...xeroConnectionConfig} />}

          <Button variant="ghost" size="icon" aria-label="Open invoice settings" onClick={onClickSettings}>
            <div className="h-9 w-9 rounded-full bg-seapoint-gray-background p-2.5">
              <Settings strokeWidth={1.5} className="h-full w-full" />
            </div>
          </Button>

          <Button variant="default" iconLeft={<FileUp />} onClick={onUploadManualInvoice}>
            Upload Invoice
          </Button>
        </Inline>
      </Inline>

      <Stack space={3} align="stretch">
        <Inline space={5} wrap="nowrap">
          {invoicesMetrics.map((metric) => (
            <InvoicesMetricCard
              key={metric.variant}
              variant={metric.variant}
              metric={metric.metric}
              normalizedAmount={metric.normalizedAmount}
              currency={metric.currency}
            />
          ))}
        </Inline>

        {emailScanningProgressConfig.isVisible && (
          <InvoiceScanningProgressCard
            variant={emailScanningProgressConfig.status}
            invoiceCount={emailScanningProgressConfig.invoiceCount}
            onHideCard={emailScanningProgressConfig.onHideCard}
          />
        )}
      </Stack>

      <InvoiceDataSection
        evidenceDataConfig={evidenceDataConfig}
        isEmailConnected={emailConnectionConfig.isConnected}
        isFetchingDocumentUrl={isFetchingDocumentUrl}
        selectedEvidenceId={selectedEvidenceId}
        approvingEvidenceId={approvingEvidenceId}
        onConnectGmail={emailConnectionConfig.onConnect}
        onReviewInvoice={onReviewInvoice}
        onSelectMenuItem={onSelectMenuItem}
        onDataSorted={onDataSorted}
        onApproveInvoice={onApproveInvoice}
        onReviewTransaction={onReviewTransaction}
      />
    </Stack>
  );
};
